import type {
	AssistantMessage,
	NL2CypherResponse,
	NL2CypherSuccessResponse,
} from '@atlassian/twg-query-builder';

import type { Message } from '../../../../../contexts/AsideContext';

export type ChatLLMResponse = {
	content: string;
	rawCypherQuery?: string;
};

const isSuccessResponse = (response: NL2CypherResponse): response is NL2CypherSuccessResponse => {
	return 'cypherQuery' in response;
};

/**
 * Builds a formatted Cypher query with explanatory comments above each line
 */
const buildAnnotatedQuery = (
	cypherExplanation: NL2CypherSuccessResponse['cypherExplanation'],
): string => {
	const { lineByLineExplanations = [] } = cypherExplanation;
	const lines: string[] = lineByLineExplanations.flatMap((line) => {
		return [`// ${line.explanation}`, line.code];
	});
	return lines.join('\n');
};

/**
 * Formats the response from NL2Cypher into a readable assistant message
 */
export const formatResponse = (res: NL2CypherResponse): ChatLLMResponse => {
	if (!isSuccessResponse(res)) {
		return {
			content: `I encountered an error: ${res.error.message}`,
		};
	}
	if (res.errors !== undefined && res.errors.length > 0) {
		return {
			content: `I couldn't process that request: ${res.errors[0].message}`,
		};
	}

	const parts: string[] = [];

	if (res.summary.length !== 0) {
		parts.push(res.summary);
	}

	// Build the Cypher query with annotations if cypherExplanation is available
	const { cypherExplanation, cypherQuery } = res;
	const displayQuery = cypherExplanation ? buildAnnotatedQuery(cypherExplanation) : cypherQuery;
	const rawQuery = cypherExplanation?.formattedQuery ?? cypherQuery;

	if (displayQuery) {
		parts.push('\n```cypher\n' + displayQuery + '\n```');
	} else {
		parts.push("I processed your request but didn't get a Cypher query back.");
	}

	return {
		content: parts.join('\n') || "I processed your request but didn't get a specific response.",
		rawCypherQuery: rawQuery,
	};
};

/**
 * Converts chat messages to the format expected by the NL2Cypher API
 */
export const messagesToApiFormat = (messages: Message[]): AssistantMessage[] => {
	return messages
		.filter((msg) => msg.id !== 'welcome-message') // Exclude welcome message
		.map((msg) => ({
			role: msg.role,
			content: [{ type: 'text' as const, text: msg.content }],
		}));
};

export const buildNextUserMessage = (currentCypherQuery: string, userMessage: string): string => {
	if (currentCypherQuery === '') {
		return userMessage;
	}
	return `${userMessage}\n\nCurrent query in the editor:\n\`\`\`cypher\n${currentCypherQuery}\n\`\`\``;
};
